import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, Mail } from "lucide-react";
import { LEGAL_CONTACT, LEGAL_PAGES, LEGAL_REGIONS, LEGAL_UPDATED } from "./legalContent";
import { useLang } from "../i18n";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const LegalPage = ({ page }) => {
  const content = LEGAL_PAGES[page];
  const [region, setRegion] = useState("in");
  const regionInfo = LEGAL_REGIONS[region];
  const { lang, t } = useLang();

  return (
    <main data-testid={`legal-page-${content.slug}`} className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(139,92,246,0.12),transparent)]" />

      {/* HERO */}
      <section className="relative z-10 mx-auto w-full max-w-4xl px-6 pb-10 pt-40 text-center md:pt-48">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <span className="badge-animated inline-flex items-center gap-3 rounded-full px-6 py-3 backdrop-blur">
            <span className="text-base font-medium text-white/85">{content.eyebrow}</span>
          </span>
          <h1 className="mx-auto mt-10 max-w-3xl font-display text-[2rem] font-bold leading-[1.08] tracking-tight sm:text-[2.75rem] lg:text-[3.25rem]">
            {content.title}
          </h1>
          <p className="mt-5 font-mono text-sm uppercase tracking-[0.25em] text-white/40">
            The Lifespan Project · Last updated {LEGAL_UPDATED}
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-lg font-light leading-relaxed text-fog">
            {content.intro}
          </p>
          {lang !== "en" && (
            <p data-testid="legal-english-notice" className="mx-auto mt-5 inline-block rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-light text-fog">
              {t("legal.notice", "This legal document is provided in English to preserve its exact meaning.")}
            </p>
          )}
        </motion.div>
      </section>

      {/* REGION SWITCH */}
      <section className="relative z-10 mx-auto max-w-4xl px-6">
        <motion.div {...fade()} className="rounded-3xl border border-white/10 bg-surface p-6 text-center md:p-8">
          <p className="flex items-center justify-center gap-2 font-mono text-sm uppercase tracking-[0.25em] text-white/50">
            <Globe2 className="h-4 w-4 text-cyan-300" />
            View this {content.slug === "faq" ? "page" : "policy"} for
          </p>
          <div data-testid="legal-region-switch" className="mx-auto mt-5 flex w-fit flex-wrap justify-center rounded-full border border-white/10 bg-void/60 p-1.5">
            {Object.entries(LEGAL_REGIONS).map(([id, r]) => (
              <button
                key={id}
                type="button"
                data-testid={`legal-region-${id}`}
                onClick={() => setRegion(id)}
                className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-300 sm:px-5 sm:text-base ${
                  region === id ? "bg-gradient-accent text-white" : "text-fog hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 py-16 md:py-20">
        <div className="grid gap-6">
          {content.sections.map((s, i) => (
            <motion.div
              key={s.h}
              {...fade(Math.min(i * 0.04, 0.2))}
              data-testid={`legal-section-${i + 1}`}
              className="rounded-2xl border border-white/10 bg-surface p-8 text-left"
            >
              <h2 className="font-display text-xl font-semibold tracking-tight md:text-2xl">{s.h}</h2>
              {s.body.map((p) => (
                <p key={p.slice(0, 32)} className="mt-4 text-base font-light leading-relaxed text-fog">
                  {p}
                </p>
              ))}
            </motion.div>
          ))}

          {/* REGIONAL BLOCK */}
          <motion.div
            {...fade(0.1)}
            data-testid="legal-regional-block"
            className="rounded-2xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px]"
          >
            <div className="rounded-2xl bg-surface p-8 text-left">
              <p className="text-gradient font-mono text-sm uppercase tracking-[0.25em]">
                {regionInfo.name}
              </p>
              <h2 className="mt-3 font-display text-xl font-semibold tracking-tight md:text-2xl">
                {content.slug === "refund"
                  ? "How this policy applies to you"
                  : content.slug === "faq"
                    ? "Notes for your region"
                    : content.slug === "privacy"
                      ? "Your rights where you live"
                      : "Governing law and your rights"}
              </h2>
              {content.regional[region].map((p) => (
                <p key={p.slice(0, 32)} className="mt-4 text-base font-light leading-relaxed text-fog">
                  {p}
                </p>
              ))}
            </div>
          </motion.div>

          {/* CONTACT */}
          <motion.div {...fade(0.12)} className="rounded-2xl border border-white/10 bg-surface p-8 text-center">
            <Mail className="mx-auto h-6 w-6 text-cyan-300" />
            <p className="mt-4 text-base font-light leading-relaxed text-fog">
              Questions about {content.title.toLowerCase()}? Write to us at
            </p>
            <a
              href={`mailto:${LEGAL_CONTACT}`}
              data-testid="legal-contact-link"
              className="text-gradient link-underline mt-2 inline-block text-lg font-semibold"
            >
              {LEGAL_CONTACT}
            </a>
            <p className="mt-6 text-sm font-light text-white/40">
              Related:{" "}
              <Link to="/terms" data-testid="legal-nav-terms" className="link-underline text-fog transition-colors hover:text-white">Terms</Link>
              {" · "}
              <Link to="/privacy" data-testid="legal-nav-privacy" className="link-underline text-fog transition-colors hover:text-white">Privacy Policy</Link>
              {" · "}
              <Link to="/refund" data-testid="legal-nav-refund" className="link-underline text-fog transition-colors hover:text-white">Refund Policy</Link>
              {" · "}
              <Link to="/faq" data-testid="legal-nav-faq" className="link-underline text-fog transition-colors hover:text-white">FAQs</Link>
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default LegalPage;
